import { type ReactElement, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useSearchParams } from "react-router";

import {
  CalendarLayoutForm,
  CalendarStyleForm,
  DESIGN_ELEMENTS,
  type DesignElement,
  DesignPreview,
  DoorNumberForm,
  PlaceholderImageForm,
  elementsFor,
} from "@/components/CalendarDesign";
import { DoorWindowSettings, doorWindowOf } from "@/components/DoorWindowSettings";
import { EmbedLookForm } from "@/components/EmbedSettings";
import { EditorLayout } from "@/components/PreviewLayout";
import { REFUSED_EVENT, useSaveBarDirty } from "@/components/SaveBar";
import { LOOK_GROUPS, StyleTransfer, copyableGroups } from "@/components/StyleTransfer";
import { Button } from "@/components/ui/button";
import { type ActionDetail, mediaSrc } from "@/lib/api";
import { draftOf } from "@/lib/calendar-draft";
import { DESIGN_TAB_ELEMENTS, type BackofficeElement, can } from "@/lib/labels";
import { navLinkClassName } from "@/lib/utils";
import { DetailState, useActionContext, useActionDetail } from "@/pages/ActionPage";
import { FormEditor } from "@/pages/FormPage";

/** The views of the design tab, by their value of `?ansicht=`; the calendar has none. */
const VIEWS = [
  ["kalender", "Kalender"],
  ["fenster", "Türchen-Fenster"],
  ["formular", "Formular"],
] as const;
type View = (typeof VIEWS)[number][0];

function viewOf(search: URLSearchParams): View {
  const asked = search.get("ansicht");
  return VIEWS.find(([view]) => view === asked)?.[0] ?? "kalender";
}

/**
 * Tab „Gestaltung" of an action (`/aktionen/:actionId/gestaltung`), with the design permission:
 * everything a participant sees, in three views beside their live previews (the column view
 * stays the pattern for every screen with a visible result, IT lead 19.10.2026): the calendar,
 * the window a door opens in, and the completion form (until 19.10.2026 a tab of its own).
 *
 * The arrangement, grid, door number and placeholder picture change also while the action runs
 * (D23, D29), and so does the window a door opens in (D37); the form locks with the first open
 * door (D24).
 *
 * A click editor (D49): a click on an element of the preview, or its button in the bar, shows
 * that element's settings and no others. The other forms stay mounted, hidden, so the save bar
 * still saves and checks them; a field the bar finds invalid brings its view and element back in
 * sight. A view is mounted on its first visit and kept, so a change survives a switch.
 */
export function ActionDesignPage(): ReactElement {
  const { granted } = useActionContext();
  const detail = useActionDetail();
  if (!can(granted, ...DESIGN_TAB_ELEMENTS)) {
    return <DetailState status="forbidden" />;
  }
  if (detail.status !== "ready") {
    return <DetailState status={detail.status} />;
  }
  // Keyed by the action, so another action's page starts from its own draft.
  return <DesignBody key={detail.data.action_id} data={detail.data} reload={detail.reload} />;
}

/**
 * The loaded design tab. It holds the drafts, so the forms and the previews share every unsaved
 * change, and a reload after one form's save leaves the others' edits in place.
 */
function DesignBody({
  data,
  reload,
}: {
  readonly data: ActionDetail;
  readonly reload: () => void;
}): ReactElement {
  const { action, actions, granted, reloadOverview } = useActionContext();
  const [calendar, setCalendar] = useState(() => draftOf(data));
  const [doorWindow, setDoorWindow] = useState(() => doorWindowOf(data));
  const [look, setLook] = useState(() => data.calendar_style.embed);
  // The style transfer copies what is saved; it says so while anything on the tab differs.
  const unsaved = useSaveBarDirty();
  const ended = data.status === "finished" || data.status === "archived";
  // Each form is written by its own element (D57); one not granted stays visible, read-only,
  // because the click editor and the save bar keep every form of the tab mounted.
  const locked = (element: BackofficeElement): boolean => ended || !can(granted, element);
  // Behind the open doors of the preview: the placeholder, else the brand logo.
  const shownImage = data.placeholder_image ?? action.brand_logo ?? null;
  const previewLogo =
    shownImage === null ? null : { src: mediaSrc(shownImage.path), alt: shownImage.alt_text };
  const saved = (): void => {
    reload();
    reloadOverview();
  };

  // The view lives in the address, so a link opens it; the state follows it synchronously,
  // which the reveal below needs (the router updates the address after the render).
  const [search, setSearch] = useSearchParams();
  const [view, setViewState] = useState<View>(() => viewOf(search));
  const [seen, setSeen] = useState<ReadonlySet<View>>(() => new Set([viewOf(search)]));
  const show = (next: View): void => {
    setViewState(next);
    setSeen((current) => (current.has(next) ? current : new Set([...current, next])));
  };
  // The view the address last agreed with. An address that changes on its own is a link on
  // this page, e.g. the go-live obstacle »Ersatzinhalt fehlt« in the header, and opens its view.
  const addressed = useRef(view);
  useEffect(() => {
    const asked = viewOf(search);
    if (view === addressed.current && asked !== view) {
      addressed.current = asked;
      setViewState(asked);
      setSeen((current) => (current.has(asked) ? current : new Set([...current, asked])));
      return;
    }
    addressed.current = view;
    if (asked !== view) {
      setSearch(
        (current) => {
          const params = new URLSearchParams(current);
          if (view === "kalender") {
            params.delete("ansicht");
          } else {
            params.set("ansicht", view);
          }
          return params;
        },
        { replace: true },
      );
    }
  }, [view, search, setSearch]);

  const [chosen, setChosen] = useState<DesignElement>("anordnung");
  const elements = elementsFor(calendar);
  // An element the draft lost (closed doors of a honeycomb) hands over to the arrangement.
  const active = elements.some(([element]) => element === chosen) ? chosen : "anordnung";
  const label = DESIGN_ELEMENTS.find(([element]) => element === active)?.[1] ?? "";
  // For the reveal below, whose listener is bound once.
  const activeRef = useRef(active);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  // A click in the preview brings the settings into view on a narrow screen, where they stand
  // above the preview; a click in the bar is already beside them.
  const heading = useRef<HTMLHeadingElement>(null);
  const fromPreview = useRef(false);
  useEffect(() => {
    if (fromPreview.current) {
      fromPreview.current = false;
      const node = heading.current;
      // jsdom has no scrollIntoView.
      if (node !== null && "scrollIntoView" in node) {
        node.scrollIntoView({ block: "nearest" });
      }
    }
  }, [chosen]);

  // The save bar checks every form before it saves (`reportValidityAboveBar`). An invalid field
  // in a hidden view or part fires `invalid` first; both are shown at once, so the browser can
  // point at the field. Only the first field of one check counts. A section the server refused
  // announces itself (`REFUSED_EVENT`) and comes back in sight the same way, with its reason.
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = root.current;
    if (node === null) {
      return undefined;
    }
    let revealing = false;
    const reveal = (event: Event): void => {
      if (revealing || !(event.target instanceof Element)) {
        return;
      }
      const inView = event.target.closest<HTMLElement>("[data-design-view]")?.dataset.designView;
      // A refused form holds its parts: the chosen one stays when it is among them, so the
      // reason stands under what the person was working on; otherwise its first one.
      const part =
        event.target.closest<HTMLElement>("[data-design-panel]") ??
        (event.type === REFUSED_EVENT
          ? (event.target.querySelector<HTMLElement>(
              `[data-design-panel="${activeRef.current}"]`,
            ) ?? event.target.querySelector<HTMLElement>("[data-design-panel]"))
          : null);
      const inPanel = part?.dataset.designPanel;
      if (inView === undefined && inPanel === undefined) {
        return;
      }
      revealing = true;
      queueMicrotask(() => {
        revealing = false;
      });
      flushSync(() => {
        if (inView !== undefined) {
          setViewState(inView as View);
        }
        if (inPanel !== undefined) {
          setChosen(inPanel as DesignElement);
        }
      });
    };
    node.addEventListener("invalid", reveal, true);
    node.addEventListener(REFUSED_EVENT, reveal);
    return () => {
      node.removeEventListener("invalid", reveal, true);
      node.removeEventListener(REFUSED_EVENT, reveal);
    };
  }, []);

  const form = { detail: data, draft: calendar, onChange: setCalendar };

  return (
    <div ref={root}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div
          role="group"
          aria-label="Ansicht"
          className="inline-flex gap-1 rounded-md bg-muted p-1"
        >
          {VIEWS.map(([value, name]) => (
            <button
              key={value}
              type="button"
              aria-pressed={view === value}
              className={navLinkClassName({ isActive: view === value })}
              onClick={() => {
                show(value);
              }}
            >
              {name}
            </button>
          ))}
        </div>
        <StyleTransfer
          source={data}
          actions={actions}
          unsaved={unsaved}
          groups={copyableGroups(LOOK_GROUPS, granted)}
        />
      </div>

      <div data-design-view="kalender" hidden={view !== "kalender"}>
        <h2 className="sr-only">Kalender</h2>
        <EditorLayout
          preview={
            <DesignPreview
              detail={data}
              draft={calendar}
              logo={previewLogo}
              brandLogo={action.brand_logo ?? null}
              chosen={active}
              look={look}
              onChoose={(element) => {
                fromPreview.current = element !== active;
                setChosen(element);
              }}
            />
          }
        >
          <nav aria-label="Elemente des Kalenders">
            <ol className="flex flex-wrap gap-2">
              {elements.map(([element, name]) => (
                <li key={element}>
                  <Button
                    type="button"
                    size="sm"
                    variant={element === active ? "default" : "outline"}
                    aria-current={element === active ? "true" : undefined}
                    onClick={() => {
                      setChosen(element);
                    }}
                  >
                    {name}
                  </Button>
                </li>
              ))}
            </ol>
          </nav>
          <h3 ref={heading} className="mt-6 text-base font-semibold">
            {label}
          </h3>
          <CalendarLayoutForm
            {...form}
            readOnly={locked("calendar_layout")}
            chosen={active}
            onSaved={saved}
          />
          <CalendarStyleForm
            {...form}
            readOnly={locked("calendar_style")}
            chosen={active}
            onSaved={saved}
            hasLogo={(action.brand_logo ?? null) !== null}
          />
          <DoorNumberForm
            {...form}
            readOnly={locked("door_number_style")}
            chosen={active}
            onSaved={saved}
          />

          <div hidden={active !== "bild"} data-design-panel="bild">
            <PlaceholderImageForm
              key={data.placeholder_image?.path ?? "ohne"}
              detail={data}
              readOnly={locked("action_fallback")}
              onSaved={saved}
              mayAcceptUnscanned={can(granted, "approve_unscanned")}
            />
          </div>
          <EmbedLookForm
            data={data}
            look={look}
            onChange={setLook}
            chosen={active}
            readOnly={locked("action_embed")}
            onSaved={saved}
          />
        </EditorLayout>
      </div>

      {seen.has("fenster") ? (
        <div data-design-view="fenster" hidden={view !== "fenster"}>
          <h2 className="sr-only">Türchen-Fenster</h2>
          <DoorWindowSettings
            detail={data}
            draft={doorWindow}
            onChange={setDoorWindow}
            readOnly={locked("door_window")}
            sponsorWorkflow={can(granted, "sponsor_workflow")}
            onSaved={saved}
            fallbackReadOnly={locked("action_fallback")}
            mayAcceptUnscanned={can(granted, "approve_unscanned")}
            showUnbooked={search.get("tuerchen") === "unbelegt"}
          />
        </div>
      ) : null}

      {seen.has("formular") ? (
        <div data-design-view="formular" hidden={view !== "formular"}>
          <h2 className="sr-only">Formular</h2>
          <FormEditor
            detail={data}
            leadFields={!locked("lead_fields")}
            accountOffer={!locked("account_offer")}
            onSaved={reloadOverview}
            onInvalid={() => {
              show("formular");
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

